import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import "./CreazionePOS.css";
import {
  downloadPosPdf,
  computeIndiceRischio,
  classifyRischio,
} from "../../stampe/generatori/PosPdf";
import { useCollection } from "../../hooks/useCollection";
import { database, timestamp } from "../../firebase/config";
import { useAuthContext } from "../../hooks/useAuthContext";
import { format } from "date-fns";
import Resizer from "react-image-file-resizer";

const GEMINI_VERTEX_FN_URL =
  "https://us-central1-giuviprojects.cloudfunctions.net/geminiGenerateContentVertex";

const COMPANY_ANAGRAFICA_BASE = {
  "Ragione Sociale": "GIUVI S.R.L.",
  "Partita IVA": "07873720960",
  "Sede Legale": "Via Belgirate, 15 - 20125 Milano (MI)",
  Email: "info@giuvi.it",
  Telefono: "02/95356675",
};

const POS_OUTPUT_SCHEMA = `{
  "Frontespizio": {
    "Committente": "string",
    "Cantiere": "string",
    "IndirizzoCantiere": "string",
    "DescrizioneOpere": "string",
    "Data": "dd/mm/yyyy",
    "Revisione": "00"
  },
  "FigureSicurezza": [
    {
      "qualifica": "string",
      "nomeCognome": "string",
      "indirizzo": "string",
      "cap": "string",
      "citta": "string"
    }
  ],
  "AnagraficaAziendale": {
    "Ragione Sociale": "string",
    "Partita IVA": "string",
    "Sede Legale": "string",
    "Email": "string",
    "Telefono": "string",
    "Datore di Lavoro": "string",
    "RSPP": "string",
    "RLS": "string",
    "Medico Competente": "string",
    "Addetto Primo Soccorso": "string",
    "Addetto Antincendio": "string",
    "Addetto Evacuazione": "string",
    "Preposto": "string"
  },
  "PersonaleTurni": [
    {
      "Nome": "string",
      "Mansione": "string",
      "Turni": "08:00 - 12:00 / 13:00 - 17:00",
      "Prescrizioni": "string"
    }
  ],
  "UbicazioneNaturaOpera": {
    "ContestoOperativo": "string",
    "AccessibilitaArea": "string",
    "VincoliArea": "string",
    "RetiServiziPresenti": "string",
    "AreeLimitrofeSensibili": "string"
  },
  "BreveDescrizioneLavori": "string",
  "GestioneContratti": [
    "string"
  ],
  "ElencoMacchine": [
    {
      "nome": "string"
    }
  ],
  "OrganizzazioneCantiere": {
    "Descrizione": "string",
    "Recinzione": "string",
    "Accessi": "string",
    "Viabilita": "string",
    "AreeDeposito": "string",
    "Rifiuti": "string",
    "ServiziIgienici": "string",
    "Mensa": "string",
    "ServiziSanitari": "string",
    "Uffici": "string",
    "ImpiantiElettrici": "string"
  },
  "ValutazioneRischi": [
    {
      "fase": "string",
      "rischi": "string",
      "prevenzione": "string",
      "dpi": "string",
      "procedure": "string",
      "interferenze": "string",
      "probabilita": "number",
      "danno": "number"
    }
  ]
}`;

const normalizeLookupKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const isFilledValue = (value) => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

const pickByAliases = (source, aliases, fallback = "") => {
  if (!source || typeof source !== "object") return fallback;

  for (const alias of aliases) {
    if (Object.prototype.hasOwnProperty.call(source, alias)) {
      const value = source[alias];
      if (isFilledValue(value)) return value;
    }
  }

  const aliasKeys = new Set(aliases.map(normalizeLookupKey));
  for (const [key, value] of Object.entries(source)) {
    if (aliasKeys.has(normalizeLookupKey(key)) && isFilledValue(value)) {
      return value;
    }
  }

  return fallback;
};

const normalizeToString = (value, fallback = "") => {
  if (value == null) return fallback;
  if (typeof value === "string") return value.trim() || fallback;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => normalizeToString(item, ""))
      .filter(Boolean)
      .join(", ");
    return joined || fallback;
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const normalizeRischioScore = (value) => {
  const num = Number(String(value ?? "").replace(",", "."));
  if (!Number.isFinite(num)) return "";
  const rounded = Math.round(num);
  if (rounded < 1 || rounded > 4) return "";
  return String(rounded);
};

const buildPersonaleTurniFromSquadra = (squadraLavoro = "") =>
  String(squadraLavoro || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((worker) => {
      const match = worker.match(/^(.*?)\s*\((.*?)\)\s*$/);
      const nome = match?.[1]?.trim() || worker;
      const mansione = match?.[2]?.trim() || "Operaio Specializzato";
      return {
        Nome: nome,
        Mansione: mansione,
        Turni: "08:00 - 12:00 / 13:00 - 17:00",
        Prescrizioni: "",
      };
    });

const buildCompanyAnagrafica = (formData = {}) => ({
  ...COMPANY_ANAGRAFICA_BASE,
  "Datore di Lavoro": normalizeToString(formData.datoreLavoro),
  RSPP: normalizeToString(formData.rspp),
  RLS: normalizeToString(formData.rls),
  "Medico Competente": normalizeToString(formData.medicoCompetente),
  "Addetto Primo Soccorso": normalizeToString(formData.addettoPrimoSoccorso),
  "Addetto Antincendio": normalizeToString(formData.addettoAntincendio),
  "Addetto Evacuazione": normalizeToString(formData.addettoEvacuazione),
  Preposto: normalizeToString(formData.preposto),
});

const normalizePosOutput = (rawData, formData = {}) => {
  const source = rawData && typeof rawData === "object" ? rawData : {};
  const frontespizioSource = pickByAliases(
    source,
    ["Frontespizio", "frontespizio"],
    {},
  );
  const ubicazioneSource = pickByAliases(
    source,
    ["UbicazioneNaturaOpera", "UbicazioneOpera", "Ubicazione"],
    {},
  );
  const organizzazioneSource = pickByAliases(
    source,
    ["OrganizzazioneCantiere", "OrganizzazioneDelCantiere"],
    {},
  );
  const rischiSource = pickByAliases(
    source,
    ["ValutazioneRischi", "AnalisiRischi", "Rischi"],
    [],
  );
  const macchineSource = pickByAliases(
    source,
    ["ElencoMacchine", "Macchine", "Attrezzature"],
    [],
  );
  const contrattiSource = pickByAliases(
    source,
    ["GestioneContratti", "Contratti", "DocumentazioneCantiere"],
    [],
  );
  const personaleSource = pickByAliases(
    source,
    ["PersonaleTurni", "SquadraLavoro", "Personale"],
    [],
  );
  const figureSource = pickByAliases(
    source,
    ["FigureSicurezza", "FigureResponsabiliSicurezza", "Figure"],
    [],
  );

  const frontespizio =
    frontespizioSource && typeof frontespizioSource === "object"
      ? frontespizioSource
      : {};
  const ubicazione =
    ubicazioneSource && typeof ubicazioneSource === "object"
      ? ubicazioneSource
      : {};
  const organizzazione =
    organizzazioneSource && typeof organizzazioneSource === "object"
      ? organizzazioneSource
      : {};

  return {
    Frontespizio: {
      Committente: normalizeToString(
        pickByAliases(frontespizio, ["Committente"], formData.committente),
      ),
      Cantiere: normalizeToString(
        pickByAliases(
          frontespizio,
          ["Cantiere", "NomeCantiere", "UbicazioneCantiere"],
          formData.indirizzoCantiere,
        ),
      ),
      IndirizzoCantiere: normalizeToString(
        pickByAliases(
          frontespizio,
          ["IndirizzoCantiere", "Indirizzo", "LuogoLavori"],
          formData.indirizzoCantiere,
        ),
      ),
      DescrizioneOpere: normalizeToString(
        pickByAliases(
          frontespizio,
          ["DescrizioneOpere", "OggettoLavori", "DescrizioneLavori"],
          formData.descrizioneOpere,
        ),
      ),
      Data: normalizeToString(
        pickByAliases(
          frontespizio,
          ["Data", "DataRedazione"],
          formData.dataRedazione,
        ),
      ),
      Revisione: normalizeToString(
        pickByAliases(frontespizio, ["Revisione", "Versione"], "00"),
      ),
    },
    FigureSicurezza: (Array.isArray(figureSource) ? figureSource : [])
      .map((item) => {
        if (item && typeof item === "object") {
          return {
            qualifica: normalizeToString(
              pickByAliases(item, ["qualifica", "ruolo", "figura"], "N.D."),
            ),
            nomeCognome: normalizeToString(
              pickByAliases(
                item,
                ["nomeCognome", "nominativo", "nome", "ragioneSociale"],
                "N.D.",
              ),
            ),
            indirizzo: normalizeToString(
              pickByAliases(item, ["indirizzo", "sede"], "N.D."),
            ),
            cap: normalizeToString(pickByAliases(item, ["cap"], "N.D.")),
            citta: normalizeToString(
              pickByAliases(item, ["citta", "città", "comune"], "N.D."),
            ),
          };
        }

        return {
          qualifica: "Figura sicurezza",
          nomeCognome: normalizeToString(item, "N.D."),
          indirizzo: "N.D.",
          cap: "N.D.",
          citta: "N.D.",
        };
      })
      .filter(
        (item) =>
          item.qualifica &&
          item.qualifica !== "N.D." &&
          item.nomeCognome &&
          item.nomeCognome !== "N.D.",
      ),
    AnagraficaAziendale: buildCompanyAnagrafica(formData),
    PersonaleTurni:
      Array.isArray(personaleSource) && personaleSource.length > 0
        ? personaleSource.map((item) => {
            if (item && typeof item === "object") {
              return {
                Nome: normalizeToString(
                  pickByAliases(item, ["Nome", "nome", "NomeCognome"], ""),
                ),
                Mansione: normalizeToString(
                  pickByAliases(
                    item,
                    ["Mansione", "mansione", "Ruolo"],
                    "Operaio Specializzato",
                  ),
                ),
                Turni: normalizeToString(
                  pickByAliases(
                    item,
                    ["Turni", "turni", "Orario"],
                    "08:00 - 12:00 / 13:00 - 17:00",
                  ),
                ),
                Prescrizioni: normalizeToString(
                  pickByAliases(
                    item,
                    [
                      "Prescrizioni",
                      "prescrizioni",
                      "prescrizioniSpecifiche",
                      "prescrizioniLavoratore",
                    ],
                    "",
                  ),
                ),
              };
            }

            return {
              Nome: normalizeToString(item, ""),
              Mansione: "Operaio Specializzato",
              Turni: "08:00 - 12:00 / 13:00 - 17:00",
              Prescrizioni: "",
            };
          })
        : buildPersonaleTurniFromSquadra(formData.squadraLavoro),
    UbicazioneNaturaOpera:
      typeof ubicazioneSource === "string"
        ? {
            ContestoOperativo: normalizeToString(ubicazioneSource),
            AccessibilitaArea: "",
            VincoliArea: "",
            RetiServiziPresenti: "",
            AreeLimitrofeSensibili: "",
          }
        : {
            ContestoOperativo: normalizeToString(
              pickByAliases(
                ubicazione,
                ["ContestoOperativo", "Contesto", "TipologiaArea"],
                "",
              ),
            ),
            AccessibilitaArea: normalizeToString(
              pickByAliases(
                ubicazione,
                ["AccessibilitaArea", "AccessibilitàArea", "Accessibilita"],
                "",
              ),
            ),
            VincoliArea: normalizeToString(
              pickByAliases(
                ubicazione,
                ["VincoliArea", "VincoliAmbientali"],
                "",
              ),
            ),
            RetiServiziPresenti: normalizeToString(
              pickByAliases(
                ubicazione,
                ["RetiServiziPresenti", "ServiziPresenti"],
                "",
              ),
            ),
            AreeLimitrofeSensibili: normalizeToString(
              pickByAliases(
                ubicazione,
                [
                  "AreeLimitrofeSensibili",
                  "AreeSensibili",
                  "InterferenzeAmbientali",
                ],
                "",
              ),
            ),
          },
    BreveDescrizioneLavori: normalizeToString(
      pickByAliases(
        source,
        [
          "BreveDescrizioneLavori",
          "DescrizioneLavoriBreve",
          "SintesiLavorazioni",
        ],
        formData.descrizioneOpere,
      ),
    ),
    GestioneContratti: Array.isArray(contrattiSource)
      ? contrattiSource.map((item) => normalizeToString(item)).filter(Boolean)
      : normalizeToString(contrattiSource)
          .split(/\n|;/)
          .map((item) => item.trim())
          .filter(Boolean),
    ElencoMacchine: Array.isArray(macchineSource)
      ? macchineSource.map((item) => {
          if (item && typeof item === "object") {
            return {
              nome: normalizeToString(
                pickByAliases(item, ["nome", "descrizione", "text"], ""),
              ),
            };
          }
          return { nome: normalizeToString(item, "") };
        })
      : normalizeToString(macchineSource)
          .split(/\n|,|;/)
          .map((item) => item.trim())
          .filter(Boolean)
          .map((item) => ({ nome: item })),
    OrganizzazioneCantiere: {
      Descrizione: normalizeToString(
        pickByAliases(organizzazione, ["Descrizione"], ""),
      ),
      Recinzione: normalizeToString(
        pickByAliases(organizzazione, ["Recinzione"], ""),
      ),
      Accessi: normalizeToString(
        pickByAliases(organizzazione, ["Accessi"], ""),
      ),
      Viabilita: normalizeToString(
        pickByAliases(organizzazione, ["Viabilita", "Viabilità"], ""),
      ),
      AreeDeposito: normalizeToString(
        pickByAliases(
          organizzazione,
          ["AreeDeposito", "DepositoMateriali"],
          "",
        ),
      ),
      Rifiuti: normalizeToString(
        pickByAliases(organizzazione, ["Rifiuti", "SmaltimentoRifiuti"], ""),
      ),
      ServiziIgienici: normalizeToString(
        pickByAliases(organizzazione, ["ServiziIgienici"], ""),
      ),
      Mensa: normalizeToString(pickByAliases(organizzazione, ["Mensa"], "")),
      ServiziSanitari: normalizeToString(
        pickByAliases(organizzazione, ["ServiziSanitari"], ""),
      ),
      Uffici: normalizeToString(pickByAliases(organizzazione, ["Uffici"], "")),
      ImpiantiElettrici: normalizeToString(
        pickByAliases(
          organizzazione,
          ["ImpiantiElettrici", "ImpiantoElettrico"],
          "",
        ),
      ),
    },
    ValutazioneRischi: (Array.isArray(rischiSource) ? rischiSource : [])
      .map((item) => {
        const rischio = item && typeof item === "object" ? item : {};
        const probabilita = normalizeRischioScore(
          pickByAliases(
            rischio,
            ["probabilita", "P", "probabilitaRischio"],
            "",
          ),
        );
        const danno = normalizeRischioScore(
          pickByAliases(
            rischio,
            ["danno", "D", "gravita", "Gravita", "dannoPotenziale"],
            "",
          ),
        );
        const indiceRischio = computeIndiceRischio(probabilita, danno);
        return {
          fase: normalizeToString(
            pickByAliases(rischio, ["fase", "Fase"], "Fase operativa"),
          ),
          rischi: normalizeToString(
            pickByAliases(rischio, ["rischi", "Rischi", "pericoli"], ""),
          ),
          prevenzione: normalizeToString(
            pickByAliases(
              rischio,
              ["prevenzione", "misurePrevenzione", "misure"],
              "",
            ),
          ),
          dpi: normalizeToString(
            pickByAliases(
              rischio,
              ["dpi", "DPI", "dispositiviProtezioneIndividuale"],
              "",
            ),
          ),
          procedure: normalizeToString(
            pickByAliases(
              rischio,
              ["procedure", "procedureOperative", "procedureOperativeStandard"],
              "",
            ),
          ),
          interferenze: normalizeToString(
            pickByAliases(
              rischio,
              ["interferenze", "coordinamento", "interferenzeCoordinamento"],
              "",
            ),
          ),
          probabilita,
          danno,
          indiceRischio: indiceRischio != null ? String(indiceRischio) : "",
          livelloRischio:
            indiceRischio != null ? classifyRischio(indiceRischio).livello : "",
        };
      })
      .filter((item) => isFilledValue(item.fase)),
  };
};

const createEmptyAppaltatrice = () => ({
  ragioneSociale: "",
  indirizzo: "",
  piva: "",
  telefono: "",
  email: "",
  rappresentante: "",
});

export default function CreazionePOS() {
  const { user } = useAuthContext();
  const { documents: users } = useCollection("users");
  const { documents: archivioPosDocs } = useCollection(
    "posArchivio",
    null,
    ["printedAt", "desc"],
    200,
  );
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const buildInitialFormData = () => ({
    committente: "",
    indirizzoCantiere: "",
    descrizioneOpere: "",
    squadraLavoro: "",
    preposto: "",
    datoreLavoro: "Roberto Ferri",
    rspp: "Roberto Ferri",
    rls: "Gianfranco Calcagno",
    medicoCompetente: "Dr. Costa Maurizio",
    addettoPrimoSoccorso: "Massimo Sironi",
    addettoAntincendio: "Gianfranco Calcagno",
    addettoEvacuazione: "Gianfranco Calcagno",
    dataInizio: "",
    durataLavori: "",
    dataRedazione: new Date().toISOString().split("T")[0],
    dettaglioLavorazioni: "",
    macchinariSostanze: "",
    interferenze: "",
  });
  const [formData, setFormData] = useState(buildInitialFormData);
  const [subappaltatori, setSubappaltatori] = useState([]);
  const [dittaAppaltatrici, setDittaAppaltatrici] = useState([]);
  const [loading, setLoading] = useState(false);
  const [posGenerato, setPosGenerato] = useState(null);
  const [error, setError] = useState(null);
  const [sicurezzaImage, setSicurezzaImage] = useState(null);
  const [portalTarget, setPortalTarget] = useState(null);

  const [archiveSearch, setArchiveSearch] = useState("");
  const [activePosId, setActivePosId] = useState(null);
  const [activeVersion, setActiveVersion] = useState(null);
  const [archiveSaving, setArchiveSaving] = useState(false);
  const [archiveSaveError, setArchiveSaveError] = useState(null);
  const [expandedPosIds, setExpandedPosIds] = useState(() => new Set());
  const [duplicaOpen, setDuplicaOpen] = useState(false);
  const [duplicaSelectedPosId, setDuplicaSelectedPosId] = useState("");
  const [duplicaSelectedDocId, setDuplicaSelectedDocId] = useState("");
  const [posJsonDraft, setPosJsonDraft] = useState("");
  const [posJsonDirty, setPosJsonDirty] = useState(false);
  const [posJsonError, setPosJsonError] = useState(null);
  const [posJsonSource, setPosJsonSource] = useState(null);
  const [posJsonSourceText, setPosJsonSourceText] = useState("");

  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSicurezzaImage(file);
    }
  };

  const userOptions = users
    ? users.map((u) => {
        const nome = u.anagrafica?.nome || u.displayName || "Utente";
        const cognome = u.anagrafica?.cognome || "";
        const mansione = u.anagrafica?.mansione || "Mansione non specificata";
        return {
          value: u.id,
          label: `${nome} ${cognome} (${mansione})`,
          user: u,
        };
      })
    : [];

  const handleWorkerChange = (selectedOptions) => {
    setSelectedWorkers(selectedOptions || []);

    const workersString = selectedOptions
      ? selectedOptions.map((opt) => opt.label).join(", ")
      : "";

    setFormData((prev) => ({
      ...prev,
      squadraLavoro: workersString,
    }));
  };

  const archivioByPosId = useMemo(() => {
    const map = new Map();
    (archivioPosDocs || []).forEach((d) => {
      const posId = d.posId || null;
      if (!posId) return;
      if (!map.has(posId)) map.set(posId, []);
      map.get(posId).push(d);
    });
    for (const [, arr] of map) {
      arr.sort((a, b) => {
        const va = typeof a.version === "number" ? a.version : 0;
        const vb = typeof b.version === "number" ? b.version : 0;
        return vb - va;
      });
    }
    return map;
  }, [archivioPosDocs]);

  const archivioGroups = useMemo(() => {
    const q = archiveSearch.trim().toLowerCase();
    const out = [];
    for (const [posId, versions] of archivioByPosId.entries()) {
      const latest = versions[0] || null;
      const haystack =
        `${latest?.committente || ""} ${latest?.indirizzoCantiere || ""} ${latest?.preposto || ""}`.toLowerCase();
      if (q && !haystack.includes(q)) continue;
      out.push({ posId, latest, versions });
    }
    out.sort((a, b) => {
      const ta = a.latest?.printedAt?.toDate
        ? a.latest.printedAt.toDate().getTime()
        : 0;
      const tb = b.latest?.printedAt?.toDate
        ? b.latest.printedAt.toDate().getTime()
        : 0;
      return tb - ta;
    });
    return out;
  }, [archivioByPosId, archiveSearch]);

  const visibleArchiveGroups = useMemo(() => {
    const q = archiveSearch.trim();
    if (q) return archivioGroups;
    return archivioGroups.slice(0, 3);
  }, [archivioGroups, archiveSearch]);

  const posDuplicaOptions = useMemo(() => {
    return (archivioGroups || []).map((g) => {
      const latest = g.latest || null;
      const vLabel =
        typeof latest?.version === "number" ? `v${latest.version}` : "v—";
      const committente = latest?.committente || "POS";
      const indirizzo = latest?.indirizzoCantiere || "";
      const label = indirizzo
        ? `${committente} · ${indirizzo} · ${vLabel}`
        : `${committente} · ${vLabel}`;
      return { value: g.posId, label };
    });
  }, [archivioGroups]);

  const duplicaSelectedGroup = useMemo(() => {
    if (!duplicaSelectedPosId) return null;
    return (
      (archivioGroups || []).find((g) => g.posId === duplicaSelectedPosId) ||
      null
    );
  }, [archivioGroups, duplicaSelectedPosId]);

  const duplicaVersionOptions = useMemo(() => {
    const versions = duplicaSelectedGroup?.versions || [];
    return versions.map((v) => {
      const vLabel = typeof v.version === "number" ? `v${v.version}` : "v—";
      const printedAt =
        v?.printedAt?.toDate && v.printedAt.toDate() instanceof Date
          ? format(v.printedAt.toDate(), "dd/MM/yyyy HH:mm")
          : "—";
      return {
        value: v.id,
        label: `${vLabel} · ${printedAt}`,
      };
    });
  }, [duplicaSelectedGroup]);

  const resetToNewPos = () => {
    setActivePosId(null);
    setActiveVersion(null);
    setArchiveSaveError(null);
    setError(null);
    setSelectedWorkers([]);
    setFormData(buildInitialFormData());
    setSubappaltatori([]);
    setDittaAppaltatrici([]);
    setPosGenerato(null);
    setPosJsonDraft("");
    setPosJsonDirty(false);
    setPosJsonError(null);
    setPosJsonSource(null);
    setPosJsonSourceText("");
    setSicurezzaImage(null);
  };

  const loadArchivedPos = (doc) => {
    const snapshot = doc?.snapshot || null;
    const nextSelectedWorkers = Array.isArray(snapshot?.selectedWorkers)
      ? snapshot.selectedWorkers
          .filter((x) => x && x.value && x.label)
          .map((x) => ({ value: x.value, label: x.label }))
      : [];
    const workersString = nextSelectedWorkers.map((o) => o.label).join(", ");
    setSelectedWorkers(nextSelectedWorkers);
    setSubappaltatori(
      Array.isArray(snapshot?.subappaltatori) ? snapshot.subappaltatori : [],
    );
    setDittaAppaltatrici(
      Array.isArray(snapshot?.dittaAppaltatrici)
        ? snapshot.dittaAppaltatrici
        : [],
    );
    const nextPos = snapshot?.posGenerato
      ? normalizePosOutput(snapshot.posGenerato, snapshot?.formData || {})
      : null;
    setPosGenerato(nextPos);
    if (nextPos) {
      const asText = JSON.stringify(nextPos, null, 2);
      setPosJsonDraft(asText);
      setPosJsonDirty(false);
      setPosJsonError(null);
      setPosJsonSource(nextPos);
      setPosJsonSourceText(asText);
    } else {
      setPosJsonDraft("");
      setPosJsonDirty(false);
      setPosJsonError(null);
      setPosJsonSource(null);
      setPosJsonSourceText("");
    }
    setFormData(() => ({
      ...buildInitialFormData(),
      ...(snapshot?.formData || {}),
      squadraLavoro: workersString,
    }));
    setActivePosId(doc?.posId || null);
    setActiveVersion(typeof doc?.version === "number" ? doc.version : null);
    setArchiveSaveError(null);
    setError(null);
  };

  const openDuplicaModal = () => {
    const defaultPosId =
      duplicaSelectedPosId ||
      (posDuplicaOptions?.length ? posDuplicaOptions[0].value : "");
    const defaultGroup =
      defaultPosId &&
      (archivioGroups || []).find((g) => g.posId === defaultPosId)
        ? (archivioGroups || []).find((g) => g.posId === defaultPosId)
        : null;
    const defaultDocId = defaultGroup?.versions?.[0]?.id || "";

    setDuplicaSelectedPosId(defaultPosId);
    setDuplicaSelectedDocId(defaultDocId);
    setDuplicaOpen(true);
  };

  const handleDuplicaConfirm = () => {
    const group =
      duplicaSelectedPosId &&
      (archivioGroups || []).find((g) => g.posId === duplicaSelectedPosId);
    const doc =
      group?.versions?.find((v) => v.id === duplicaSelectedDocId) || null;
    if (!doc) return;

    loadArchivedPos(doc);
    setActivePosId(null);
    setActiveVersion(null);
    setPosGenerato(null);
    setPosJsonDraft("");
    setPosJsonDirty(false);
    setPosJsonError(null);
    setPosJsonSource(null);
    setPosJsonSourceText("");
    setSicurezzaImage(null);
    setDuplicaOpen(false);
  };

  const savePosToArchivio = async () => {
    setArchiveSaving(true);
    setArchiveSaveError(null);
    try {
      if (!user?.uid) throw new Error("Utente non autenticato");

      const snapshot = {
        formData,
        subappaltatori,
        dittaAppaltatrici,
        selectedWorkers: (selectedWorkers || []).map((o) => ({
          value: o.value,
          label: o.label,
        })),
        posGenerato: posGenerato || null,
      };

      const printedAt = timestamp.fromDate(new Date());
      const basePosId =
        activePosId || database.collection("posArchivio").doc().id;

      let nextVersion = 1;
      try {
        const lastSnap = await database
          .collection("posArchivio")
          .where("posId", "==", basePosId)
          .orderBy("version", "desc")
          .limit(1)
          .get();
        const last = lastSnap.docs?.[0]?.data?.() || null;
        const lastV = typeof last?.version === "number" ? last.version : 0;
        nextVersion = lastV + 1;
      } catch {
        nextVersion =
          (typeof activeVersion === "number" ? activeVersion : 0) + 1;
        if (nextVersion <= 0) nextVersion = 1;
      }

      const docId = `${basePosId}_v${nextVersion}`;
      await database
        .collection("posArchivio")
        .doc(docId)
        .set({
          posId: basePosId,
          version: nextVersion,
          sourceVersion:
            typeof activeVersion === "number" ? activeVersion : null,
          printedAt,
          printedBy: user.uid,
          committente: formData.committente || "",
          indirizzoCantiere: formData.indirizzoCantiere || "",
          preposto: formData.preposto || "",
          dataRedazione: formData.dataRedazione || "",
          snapshot,
        });

      setActivePosId(basePosId);
      setActiveVersion(nextVersion);
      return { posId: basePosId, version: nextVersion };
    } catch (e) {
      setArchiveSaveError(e.message);
      throw e;
    } finally {
      setArchiveSaving(false);
    }
  };

  const handleAddSubappaltatore = () => {
    setSubappaltatori([
      ...subappaltatori,
      {
        ragioneSociale: "",
        indirizzo: "",
        piva: "",
        telefono: "",
        email: "",
        rappresentante: "",
        opereAssegnate: "",
      },
    ]);
  };

  const handleRemoveSubappaltatore = (index) => {
    const newSubs = [...subappaltatori];
    newSubs.splice(index, 1);
    setSubappaltatori(newSubs);
  };

  const handleSubappaltatoreChange = (index, field, value) => {
    const newSubs = [...subappaltatori];
    newSubs[index][field] = value;
    setSubappaltatori(newSubs);
  };

  const handleDittaAppaltatriceChange = (index, field, value) => {
    const newDitte = [...dittaAppaltatrici];
    newDitte[index][field] = value;
    setDittaAppaltatrici(newDitte);
  };

  const handleAddDittaAppaltatrice = () => {
    setDittaAppaltatrici([...dittaAppaltatrici, createEmptyAppaltatrice()]);
  };

  const handleRemoveDittaAppaltatrice = (index) => {
    const newDitte = [...dittaAppaltatrici];
    newDitte.splice(index, 1);
    setDittaAppaltatrici(newDitte);
  };

  const updatePosObjectField = (section, field, value) => {
    setPosGenerato((prev) => {
      const next = {
        ...prev,
        [section]: { ...(prev?.[section] || {}), [field]: value },
      };
      setPosJsonDraft(JSON.stringify(next, null, 2));
      setPosJsonDirty(true);
      setPosJsonError(null);
      return next;
    });
  };

  const updatePosStringField = (section, value) => {
    setPosGenerato((prev) => {
      const next = { ...prev, [section]: value };
      setPosJsonDraft(JSON.stringify(next, null, 2));
      setPosJsonDirty(true);
      setPosJsonError(null);
      return next;
    });
  };

  const updatePosArrayItem = (section, index, field, value) => {
    setPosGenerato((prev) => {
      const arr = Array.isArray(prev?.[section]) ? [...prev[section]] : [];
      if (arr[index] && typeof arr[index] === "object") {
        const updated = { ...arr[index], [field]: value };
        if (
          section === "ValutazioneRischi" &&
          (field === "probabilita" || field === "danno")
        ) {
          const indice = computeIndiceRischio(
            updated.probabilita,
            updated.danno,
          );
          updated.indiceRischio = indice != null ? String(indice) : "";
          updated.livelloRischio =
            indice != null ? classifyRischio(indice).livello : "";
        }
        arr[index] = updated;
      } else {
        arr[index] = value;
      }
      const next = { ...prev, [section]: arr };
      setPosJsonDraft(JSON.stringify(next, null, 2));
      setPosJsonDirty(true);
      setPosJsonError(null);
      return next;
    });
  };

  const updatePosArrayStringItem = (section, index, value) => {
    setPosGenerato((prev) => {
      const arr = Array.isArray(prev?.[section]) ? [...prev[section]] : [];
      arr[index] = value;
      const next = { ...prev, [section]: arr };
      setPosJsonDraft(JSON.stringify(next, null, 2));
      setPosJsonDirty(true);
      setPosJsonError(null);
      return next;
    });
  };

  const addPosArrayItem = (section, defaultItem) => {
    setPosGenerato((prev) => {
      const arr = Array.isArray(prev?.[section]) ? [...prev[section]] : [];
      arr.push(defaultItem);
      const next = { ...prev, [section]: arr };
      setPosJsonDraft(JSON.stringify(next, null, 2));
      setPosJsonDirty(true);
      setPosJsonError(null);
      return next;
    });
  };

  const removePosArrayItem = (section, index) => {
    setPosGenerato((prev) => {
      const arr = Array.isArray(prev?.[section]) ? [...prev[section]] : [];
      arr.splice(index, 1);
      const next = { ...prev, [section]: arr };
      setPosJsonDraft(JSON.stringify(next, null, 2));
      setPosJsonDirty(true);
      setPosJsonError(null);
      return next;
    });
  };

  const compressImageToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      try {
        Resizer.imageFileResizer(
          file,
          1600,
          1600,
          "JPEG",
          72,
          0,
          (uri) => resolve(uri),
          "base64",
        );
      } catch (e) {
        reject(e);
      }
    });

  const fileToGenerativePart = async (file) => {
    if (file?.type && String(file.type).startsWith("image/")) {
      try {
        const dataUrl = await compressImageToDataUrl(file);
        const base64String = String(dataUrl).split(",")[1] || "";
        return {
          inlineData: {
            data: base64String,
            mimeType: "image/jpeg",
          },
        };
      } catch (_) {}
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        resolve({
          inlineData: {
            data: base64String,
            mimeType: file.type,
          },
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const generaPOS = async () => {
    setLoading(true);
    setError(null);
    setPosGenerato(null);

    const companyAnagrafica = buildCompanyAnagrafica(formData);

    const prompt = `
      Agisci come un esperto Coordinatore della Sicurezza in fase di Esecuzione (CSE).
      Devi redigere una bozza di Piano Operativo di Sicurezza (POS) completa e professionale, ma con struttura JSON PERFETTAMENTE compatibile con il template PDF già esistente.
      
      Utilizza i seguenti dati specifici del cantiere:
      - Committente: ${formData.committente}
      - Indirizzo Cantiere: ${formData.indirizzoCantiere}
      - Descrizione Sintetica Opere: ${formData.descrizioneOpere}
      - Squadra di Lavoro (Lavoratori): ${formData.squadraLavoro}
      - Preposto: ${formData.preposto}
      - Datore di Lavoro: ${formData.datoreLavoro}
      - RSPP: ${formData.rspp}
      - RLS: ${formData.rls}
      - Medico Competente: ${formData.medicoCompetente}
      - Addetto Primo Soccorso: ${formData.addettoPrimoSoccorso}
      - Addetto Antincendio: ${formData.addettoAntincendio}
      - Addetto Evacuazione: ${formData.addettoEvacuazione}
      - Data Inizio Lavori: ${formData.dataInizio}
      - Durata Presunta / Data Fine: ${formData.durataLavori}
      - Dettaglio Lavorazioni (per analisi rischi): ${formData.dettaglioLavorazioni}
      - Macchinari e Sostanze: ${formData.macchinariSostanze}
      - Interferenze (altre ditte/contesti): ${formData.interferenze}
      - Immagine cartello sicurezza allegata: ${sicurezzaImage ? "SI" : "NO"}

      REGOLE OBBLIGATORIE DI COMPILAZIONE:
      1. Usa ESATTAMENTE i nomi proprietà richiesti qui sotto. Non creare chiavi alternative, sinonimi o versioni abbreviate.
      2. Restituisci SOLO le chiavi presenti nello schema seguente. Non aggiungere sezioni extra come "ProcedureEmergenza", "FormazioneInformazione", "SegnaleticaOrganizzazione" o altre chiavi non richieste.
      3. NON INVENTARE DATI. Se un dato non e presente nei dati forniti o non e leggibile nell'immagine, lascia stringa vuota "". Fa eccezione solo "FigureSicurezza", dove puoi usare "N.D." per campi mancanti della singola figura realmente visibile.
      4. "FigureSicurezza" deve contenere SOLO le figure/ruoli realmente presenti nell'immagine allegata del cartello sicurezza. Non derivare figure da conoscenza generale, dal testo del cantiere o da supposizioni. Se l'immagine non e allegata o non e leggibile, restituisci array vuoto [].
      5. "FigureSicurezza" deve essere sempre un ARRAY di oggetti con le chiavi esatte: "qualifica", "nomeCognome", "indirizzo", "cap", "citta".
      6. "AnagraficaAziendale" NON deve essere generata dall'AI. E un blocco fisso dell'applicazione con dati aziendali gia determinati. Mantieni la sezione nello schema ma valorizzala ESATTAMENTE con questi dati, senza reinterpretazioni, arricchimenti o invenzioni:
         ${JSON.stringify(companyAnagrafica, null, 2)}
      7. "PersonaleTurni" deve essere un ARRAY di oggetti con le chiavi esatte: "Nome", "Mansione", "Turni", "Prescrizioni". Il campo "Prescrizioni" deve essere SEMPRE una stringa vuota "": è compilato manualmente dall'operatore e non deve essere inventato dall'AI.
      8. "ElencoMacchine" deve essere la chiave canonica per macchine/attrezzature. Non usare "Macchine" o "Attrezzature".
      9. "OrganizzazioneCantiere" deve contenere ESATTAMENTE: "Descrizione", "Recinzione", "Accessi", "Viabilita", "AreeDeposito", "Rifiuti", "ServiziIgienici", "Mensa", "ServiziSanitari", "Uffici", "ImpiantiElettrici".
      10. "ValutazioneRischi" deve essere un ARRAY di oggetti con le chiavi esatte: "fase", "rischi", "prevenzione", "dpi", "procedure", "interferenze", "probabilita", "danno". "probabilita" e "danno" devono essere NUMERI INTERI da 1 a 4 (non stringhe).
      11. Non usare formule come "presumibilmente", "si ipotizza", "tipicamente", "verosimilmente". Compila solo ciò che deriva dai dati forniti.
      12. Se una sezione non ha dati sufficienti, restituiscila vuota ma nel formato corretto.

      Schema JSON da rispettare in modo tassativo:
      ${POS_OUTPUT_SCHEMA}

      Indicazioni di contenuto:
      - "Frontespizio": usa i dati di committente, cantiere, indirizzo, descrizione opere, data e revisione.
      - "FigureSicurezza": estrai esclusivamente le figure/ruoli effettivamente leggibili dal cartello sicurezza allegato.
      - "AnagraficaAziendale": riporta esclusivamente i dati fissi sopra forniti, senza aggiungere nulla.
      - "PersonaleTurni": ricava i lavoratori dalla squadra di lavoro fornita; lascia il campo "Prescrizioni" vuoto (compilazione manuale).
      - "UbicazioneNaturaOpera": descrivi contesto operativo, accessibilità, vincoli e aree sensibili.
      - "BreveDescrizioneLavori": max 500 caratteri, tecnica e sintetica.
      - "GestioneContratti": elenco documenti tenuti in cantiere.
      - "ElencoMacchine": elenco macchine/attrezzature necessarie ai lavori.
      - "OrganizzazioneCantiere": compila tutti i blocchi logistici richiesti.
      - "ValutazioneRischi": crea più fasi operative coerenti con il dettaglio lavorazioni e compila ogni campo in modo tecnico e discorsivo. Per ogni fase assegna la stima del rischio tramite la matrice P x D: "probabilita" (1 = improbabile, 2 = poco probabile, 3 = probabile, 4 = molto probabile) e "danno" (1 = lieve, 2 = modesta, 3 = grave, 4 = gravissima), come numeri interi da 1 a 4 coerenti con i rischi descritti nella fase.

      Restituisci SOLO il JSON puro.
    `;

    try {
      if (!user) {
        throw new Error("Autenticazione richiesta");
      }
      const idToken = await user.getIdToken();
      let base64Data = null;
      let mimeType = null;
      if (sicurezzaImage) {
        const imagePart = await fileToGenerativePart(sicurezzaImage);
        base64Data = imagePart?.inlineData?.data || null;
        mimeType = imagePart?.inlineData?.mimeType || null;
      }

      const response = await fetch(GEMINI_VERTEX_FN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          prompt,
          mimeType,
          base64Data,
          model: "gemini-2.0-flash-001",
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Errore nella chiamata a Vertex AI");
      }

      const data = await response.json();
      const textResult = data.text || "";

      let cleanResult = textResult;
      const jsonMatch = textResult.match(/```json\n([\s\S]*?)\n```/);
      if (jsonMatch && jsonMatch[1]) {
        cleanResult = jsonMatch[1];
      } else {
        const firstBrace = textResult.indexOf("{");
        const lastBrace = textResult.lastIndexOf("}");
        if (firstBrace !== -1 && lastBrace !== -1) {
          cleanResult = textResult.substring(firstBrace, lastBrace + 1);
        }
      }

      const parsed = normalizePosOutput(JSON.parse(cleanResult), formData);
      const pretty = JSON.stringify(parsed, null, 2);
      setPosGenerato(parsed);
      setPosJsonDraft(pretty);
      setPosJsonDirty(false);
      setPosJsonError(null);
      setPosJsonSource(parsed);
      setPosJsonSourceText(pretty);
    } catch (err) {
      console.error("Errore generazione POS:", err);
      setError(
        "Si è verificato un errore durante la generazione del POS: " +
          err.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    // if (!posGenerato) return; // Permetti download anche senza dati per test
    try {
      await downloadPosPdf({
        posData: posGenerato || {}, // Passa oggetto vuoto se null per evitare crash
        fileName: `POS_${formData.committente || "cantiere_test"}`,
        dataRedazione: formData.dataRedazione,
        dataInizio: formData.dataInizio,
        indirizzoCantiere: formData.indirizzoCantiere,
        durataLavori: formData.durataLavori,
        preposto: formData.preposto,
        subappaltatori: subappaltatori,
        dittaAppaltatrici,
      });
      await savePosToArchivio();
    } catch (error) {
      console.error("Errore download PDF:", error);
      setError("Errore durante la generazione del PDF: " + error.message);
    }
  };

  return (
    <div className="creazione-pos-container">
      <h1>Creazione POS con AI</h1>
      <p>
        Compila i campi sottostanti con i dati del cantiere per generare il
        Piano Operativo di Sicurezza.
      </p>

      <div className="pos-archive">
        <div className="pos-archive-head">
          <div className="pos-archive-title">
            <h2>Archivio POS</h2>
            {activePosId && (
              <div className="pos-archive-active">
                {activePosId}
                {typeof activeVersion === "number"
                  ? ` · v${activeVersion}`
                  : ""}
              </div>
            )}
          </div>
          <div className="pos-archive-controls">
            <input
              className="pos-archive-search"
              type="text"
              value={archiveSearch}
              onChange={(e) => setArchiveSearch(e.target.value)}
              placeholder="Cerca per committente / indirizzo / preposto"
            />
            <button
              type="button"
              className="pos-archive-new"
              onClick={resetToNewPos}
            >
              Nuovo POS
            </button>
            <button
              type="button"
              className="pos-archive-new"
              onClick={openDuplicaModal}
              disabled={archivioGroups.length === 0}
              title={
                archivioGroups.length === 0
                  ? "Nessun POS in archivio da duplicare"
                  : ""
              }
            >
              Nuovo con duplica
            </button>
          </div>
        </div>
        <div className="pos-archive-body">
          {archivioGroups.length === 0 ? (
            <div className="pos-archive-empty">Nessun POS in archivio</div>
          ) : (
            <>
              {!archiveSearch.trim() && archivioGroups.length > 3 && (
                <div className="pos-archive-hint">
                  Mostrati i 3 POS più recenti. Usa la ricerca per trovare i POS
                  meno recenti.
                </div>
              )}
              {visibleArchiveGroups.map((g) => {
                const isExpanded = expandedPosIds.has(g.posId);
                const latest = g.latest;
                const latestDate =
                  latest?.printedAt?.toDate &&
                  latest.printedAt.toDate() instanceof Date
                    ? format(latest.printedAt.toDate(), "dd/MM/yyyy HH:mm")
                    : "—";
                return (
                  <div key={g.posId} className="pos-archive-item">
                    <button
                      type="button"
                      className="pos-archive-item-head"
                      onClick={() => {
                        setExpandedPosIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(g.posId)) next.delete(g.posId);
                          else next.add(g.posId);
                          return next;
                        });
                      }}
                    >
                      <span className="pos-archive-arrow">
                        {isExpanded ? "▾" : "▸"}
                      </span>
                      <span className="pos-archive-main">
                        <span className="pos-archive-committente">
                          {latest?.committente || "POS"}
                        </span>
                        <span className="pos-archive-meta">
                          {latest?.indirizzoCantiere
                            ? ` · ${latest.indirizzoCantiere}`
                            : ""}
                          {typeof latest?.version === "number"
                            ? ` · v${latest.version}`
                            : ""}
                          {latestDate !== "—" ? ` · ${latestDate}` : ""}
                          {g.versions.length
                            ? ` · ${g.versions.length} versioni`
                            : ""}
                        </span>
                      </span>
                    </button>
                    {isExpanded && (
                      <div className="pos-archive-versions">
                        {g.versions.map((v) => {
                          const vDate =
                            v?.printedAt?.toDate &&
                            v.printedAt.toDate() instanceof Date
                              ? format(v.printedAt.toDate(), "dd/MM/yyyy HH:mm")
                              : "—";
                          const isActive =
                            v.posId === activePosId &&
                            v.version === activeVersion;
                          return (
                            <button
                              key={v.id}
                              type="button"
                              className={`pos-archive-version ${
                                isActive ? "active" : ""
                              }`}
                              onClick={() => loadArchivedPos(v)}
                            >
                              <span className="pos-archive-version-left">
                                {typeof v.version === "number"
                                  ? `v${v.version}`
                                  : "v—"}
                              </span>
                              <span className="pos-archive-version-right">
                                {vDate}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      <div className="pos-form-container">
        {/* SEZIONE 1: Identificazione Cantiere */}
        <div className="form-section-header">
          <h3>Identificazione Cantiere</h3>
        </div>

        <div className="form-group">
          <label>Committente</label>
          <input
            type="text"
            name="committente"
            value={formData.committente}
            onChange={handleChange}
            placeholder="Nome Cognome o Ragione Sociale"
          />
        </div>

        <div className="form-group">
          <label>Indirizzo Cantiere</label>
          <input
            type="text"
            name="indirizzoCantiere"
            value={formData.indirizzoCantiere}
            onChange={handleChange}
            placeholder="Via Roma 1, Milano"
          />
        </div>

        <div className="form-group full-width">
          <label>Descrizione Sintetica Opere</label>
          <textarea
            name="descrizioneOpere"
            value={formData.descrizioneOpere}
            onChange={handleChange}
            placeholder="Es. Ristrutturazione completa appartamento int. 4..."
            rows="2"
          />
        </div>

        {/* SEZIONE 2: Squadra di Lavoro */}
        <div className="form-section-header">
          <h3>Squadra di Lavoro</h3>
        </div>

        <div className="form-group full-width">
          <label>
            Carica Cartello di Cantiere / Notifica Preliminare (Opzionale)
          </label>
          <p className="input-helper-text">
            Carica una foto per estrarre: Progettista, Direttore Lavori, Resp.
            Lavori, CSP, CSE.
          </p>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="file-input"
          />
        </div>

        <div className="form-group full-width">
          <label>Elenco Lavoratori (Seleziona dalla lista)</label>
          <Select
            isMulti
            name="squadraLavoro"
            options={userOptions}
            value={selectedWorkers}
            onChange={handleWorkerChange}
            placeholder="Seleziona dipendenti..."
            className="basic-multi-select"
            classNamePrefix="select"
            menuPortalTarget={portalTarget}
            menuPosition="fixed"
            styles={{
              control: (base) => ({
                ...base,
                border: "1px solid #ddd",
                boxShadow: "none",
                "&:hover": { border: "1px solid #0056b3" },
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>

        {/* SEZIONE 3: Referenti Aziendali */}
        <div className="form-section-header">
          <h3>Referenti Aziendali (Sicurezza)</h3>
        </div>

        <div className="form-group">
          <label>Datore di Lavoro</label>
          <input
            type="text"
            name="datoreLavoro"
            value={formData.datoreLavoro}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>RSPP</label>
          <input
            type="text"
            name="rspp"
            value={formData.rspp}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>RLS</label>
          <input
            type="text"
            name="rls"
            value={formData.rls}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>Medico Competente</label>
          <input
            type="text"
            name="medicoCompetente"
            value={formData.medicoCompetente}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>Addetto Primo Soccorso</label>
          <input
            type="text"
            name="addettoPrimoSoccorso"
            value={formData.addettoPrimoSoccorso}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>Addetto Antincendio</label>
          <input
            type="text"
            name="addettoAntincendio"
            value={formData.addettoAntincendio}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>Addetto Evacuazione</label>
          <input
            type="text"
            name="addettoEvacuazione"
            value={formData.addettoEvacuazione}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Preposto</label>
          <input
            type="text"
            name="preposto"
            value={formData.preposto}
            onChange={handleChange}
            placeholder="Nome del Preposto"
          />
        </div>

        {/* SEZIONE 3a: Ditte Appaltatrici */}
        <div className="form-section-header">
          <h3>Ditta Appaltatrice</h3>
        </div>
        <div className="form-group full-width">
          {dittaAppaltatrici.map((app, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #ddd",
                padding: "10px",
                marginBottom: "10px",
                borderRadius: "5px",
              }}
            >
              <h4>Ditta Appaltatrice {index + 1}</h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <div>
                  <label>Ragione Sociale</label>
                  <input
                    type="text"
                    value={app.ragioneSociale}
                    onChange={(e) =>
                      handleDittaAppaltatriceChange(
                        index,
                        "ragioneSociale",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div>
                  <label>Indirizzo</label>
                  <input
                    type="text"
                    value={app.indirizzo}
                    onChange={(e) =>
                      handleDittaAppaltatriceChange(
                        index,
                        "indirizzo",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div>
                  <label>P.IVA / C.F.</label>
                  <input
                    type="text"
                    value={app.piva}
                    onChange={(e) =>
                      handleDittaAppaltatriceChange(
                        index,
                        "piva",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div>
                  <label>Telefono</label>
                  <input
                    type="text"
                    value={app.telefono}
                    onChange={(e) =>
                      handleDittaAppaltatriceChange(
                        index,
                        "telefono",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div>
                  <label>Email</label>
                  <input
                    type="text"
                    value={app.email}
                    onChange={(e) =>
                      handleDittaAppaltatriceChange(
                        index,
                        "email",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div>
                  <label>Legale Rappresentante</label>
                  <input
                    type="text"
                    value={app.rappresentante}
                    onChange={(e) =>
                      handleDittaAppaltatriceChange(
                        index,
                        "rappresentante",
                        e.target.value,
                      )
                    }
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveDittaAppaltatrice(index)}
                style={{
                  marginTop: "10px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                Rimuovi
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddDittaAppaltatrice}
            style={{
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              padding: "10px",
              cursor: "pointer",
              marginTop: "5px",
            }}
          >
            + Aggiungi Ditta Appaltatrice
          </button>
        </div>

        {/* SEZIONE 3b: Imprese Subappaltatrici */}
        <div className="form-section-header">
          <h3>Imprese Subappaltatrici</h3>
        </div>
        <div className="form-group full-width">
          {subappaltatori.map((sub, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #ddd",
                padding: "10px",
                marginBottom: "10px",
                borderRadius: "5px",
              }}
            >
              <h4>Subappaltatore {index + 1}</h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <div>
                  <label>Ragione Sociale</label>
                  <input
                    type="text"
                    value={sub.ragioneSociale}
                    onChange={(e) =>
                      handleSubappaltatoreChange(
                        index,
                        "ragioneSociale",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div>
                  <label>Indirizzo</label>
                  <input
                    type="text"
                    value={sub.indirizzo}
                    onChange={(e) =>
                      handleSubappaltatoreChange(
                        index,
                        "indirizzo",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div>
                  <label>P.IVA / C.F.</label>
                  <input
                    type="text"
                    value={sub.piva}
                    onChange={(e) =>
                      handleSubappaltatoreChange(index, "piva", e.target.value)
                    }
                  />
                </div>
                <div>
                  <label>Telefono</label>
                  <input
                    type="text"
                    value={sub.telefono}
                    onChange={(e) =>
                      handleSubappaltatoreChange(
                        index,
                        "telefono",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div>
                  <label>Email</label>
                  <input
                    type="text"
                    value={sub.email}
                    onChange={(e) =>
                      handleSubappaltatoreChange(index, "email", e.target.value)
                    }
                  />
                </div>
                <div>
                  <label>Legale Rappresentante</label>
                  <input
                    type="text"
                    value={sub.rappresentante}
                    onChange={(e) =>
                      handleSubappaltatoreChange(
                        index,
                        "rappresentante",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div>
                  <label>Opere Assegnate</label>
                  <input
                    type="text"
                    value={sub.opereAssegnate}
                    onChange={(e) =>
                      handleSubappaltatoreChange(
                        index,
                        "opereAssegnate",
                        e.target.value,
                      )
                    }
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveSubappaltatore(index)}
                style={{
                  marginTop: "10px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                Rimuovi
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddSubappaltatore}
            style={{
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              padding: "10px",
              cursor: "pointer",
              marginTop: "5px",
            }}
          >
            + Aggiungi Subappaltatore
          </button>
        </div>

        {/* SEZIONE 3: Tempistiche */}
        <div className="form-section-header">
          <h3>Tempistiche</h3>
        </div>

        <div className="form-group">
          <label>Data Inizio Lavori</label>
          <input
            type="date"
            name="dataInizio"
            value={formData.dataInizio}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Data Redazione POS</label>
          <input
            type="date"
            name="dataRedazione"
            value={formData.dataRedazione}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Durata Presunta / Data Fine</label>
          <input
            type="text"
            name="durataLavori"
            value={formData.durataLavori}
            onChange={handleChange}
            placeholder="Es. 60 giorni o 31/12/2024"
          />
        </div>

        {/* SEZIONE 4: Dettagli Tecnici */}
        <div className="form-section-header">
          <h3>Dettagli Tecnici e Rischi</h3>
        </div>

        <div className="form-group full-width">
          <label>Dettaglio Lavorazioni (Fasi Tecniche)</label>
          <textarea
            name="dettaglioLavorazioni"
            value={formData.dettaglioLavorazioni}
            onChange={handleChange}
            placeholder="Es. Demolizione tramezzi, rifacimento impianto idraulico, posa pavimenti..."
            rows="3"
          />
        </div>

        <div className="form-group full-width">
          <label>Macchinari e Sostanze</label>
          <textarea
            name="macchinariSostanze"
            value={formData.macchinariSostanze}
            onChange={handleChange}
            placeholder="Es. Martello demolitore, flessibile, malta cementizia, vernici..."
            rows="2"
          />
        </div>

        <div className="form-group full-width">
          <label>Interferenze e Contesti Particolari</label>
          <textarea
            name="interferenze"
            value={formData.interferenze}
            onChange={handleChange}
            placeholder="Es. Presenza ditta idraulica esterna, uffici aperti al pubblico..."
            rows="2"
          />
        </div>

        <div className="form-actions">
          <button
            className="btn-generate"
            onClick={generaPOS}
            disabled={loading || !formData.committente}
          >
            {loading ? (
              <>
                <span className="spinner-small"></span> Generazione POS in
                corso...
              </>
            ) : (
              "Genera POS Completo"
            )}
          </button>

          <button
            className="btn-download-pdf"
            onClick={handleDownloadPdf}
            style={{ marginLeft: "10px", backgroundColor: "#6c757d" }} // Stile temporaneo per distinguerlo
            disabled={archiveSaving}
          >
            {archiveSaving ? "Salvataggio..." : "Scarica PDF"}
          </button>
        </div>
        {archiveSaveError && (
          <div className="error-message">
            Errore salvataggio archivio: {archiveSaveError}
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {posGenerato && (
        <div className="pos-result">
          <div
            className="pos-result-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2>Piano Operativo di Sicurezza (Bozza)</h2>
          </div>

          <div className="pos-section-result">
            <h3>1. Frontespizio</h3>
            <div className="pos-edit-grid">
              <div className="pos-edit-field">
                <label>Committente</label>
                <input
                  type="text"
                  value={posGenerato.Frontespizio?.Committente || ""}
                  onChange={(e) =>
                    updatePosObjectField(
                      "Frontespizio",
                      "Committente",
                      e.target.value,
                    )
                  }
                />
              </div>
              <div className="pos-edit-field">
                <label>Cantiere</label>
                <input
                  type="text"
                  value={posGenerato.Frontespizio?.Cantiere || ""}
                  onChange={(e) =>
                    updatePosObjectField(
                      "Frontespizio",
                      "Cantiere",
                      e.target.value,
                    )
                  }
                />
              </div>
              <div className="pos-edit-field full-width">
                <label>Indirizzo Cantiere</label>
                <input
                  type="text"
                  value={posGenerato.Frontespizio?.IndirizzoCantiere || ""}
                  onChange={(e) =>
                    updatePosObjectField(
                      "Frontespizio",
                      "IndirizzoCantiere",
                      e.target.value,
                    )
                  }
                />
              </div>
              <div className="pos-edit-field full-width">
                <label>Descrizione Opere</label>
                <textarea
                  rows={2}
                  value={posGenerato.Frontespizio?.DescrizioneOpere || ""}
                  onChange={(e) =>
                    updatePosObjectField(
                      "Frontespizio",
                      "DescrizioneOpere",
                      e.target.value,
                    )
                  }
                />
              </div>
              <div className="pos-edit-field">
                <label>Data</label>
                <input
                  type="text"
                  value={posGenerato.Frontespizio?.Data || ""}
                  onChange={(e) =>
                    updatePosObjectField("Frontespizio", "Data", e.target.value)
                  }
                />
              </div>
              <div className="pos-edit-field">
                <label>Revisione</label>
                <input
                  type="text"
                  value={posGenerato.Frontespizio?.Revisione || ""}
                  onChange={(e) =>
                    updatePosObjectField(
                      "Frontespizio",
                      "Revisione",
                      e.target.value,
                    )
                  }
                />
              </div>
            </div>
          </div>

          <div className="pos-section-result">
            <div className="pos-section-head">
              <h3>2. Figure per la Sicurezza</h3>
              <button
                type="button"
                className="pos-add-btn"
                onClick={() =>
                  addPosArrayItem("FigureSicurezza", {
                    qualifica: "",
                    nomeCognome: "",
                    indirizzo: "",
                    cap: "",
                    citta: "",
                  })
                }
              >
                + Aggiungi figura
              </button>
            </div>
            {posGenerato.FigureSicurezza?.length ? (
              <div className="pos-edit-table-wrap">
                <table className="pos-edit-table">
                  <thead>
                    <tr>
                      <th>Qualifica</th>
                      <th>Nome Cognome</th>
                      <th>Indirizzo</th>
                      <th>CAP</th>
                      <th>Città</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {posGenerato.FigureSicurezza.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            value={item.qualifica || ""}
                            onChange={(e) =>
                              updatePosArrayItem(
                                "FigureSicurezza",
                                index,
                                "qualifica",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={item.nomeCognome || ""}
                            onChange={(e) =>
                              updatePosArrayItem(
                                "FigureSicurezza",
                                index,
                                "nomeCognome",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={item.indirizzo || ""}
                            onChange={(e) =>
                              updatePosArrayItem(
                                "FigureSicurezza",
                                index,
                                "indirizzo",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={item.cap || ""}
                            onChange={(e) =>
                              updatePosArrayItem(
                                "FigureSicurezza",
                                index,
                                "cap",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={item.citta || ""}
                            onChange={(e) =>
                              updatePosArrayItem(
                                "FigureSicurezza",
                                index,
                                "citta",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="pos-remove-btn"
                            onClick={() =>
                              removePosArrayItem("FigureSicurezza", index)
                            }
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="pos-empty-hint">Nessuna figura inserita.</p>
            )}
          </div>

          <div className="pos-section-result">
            <h3>3. Anagrafica Aziendale</h3>
            <div className="pos-edit-grid">
              {Object.entries(posGenerato.AnagraficaAziendale || {}).map(
                ([key, value]) => (
                  <div
                    key={key}
                    className={`pos-edit-field ${
                      key.length > 16 ? "full-width" : ""
                    }`}
                  >
                    <label>{key}</label>
                    <input
                      type="text"
                      value={value || ""}
                      onChange={(e) =>
                        updatePosObjectField(
                          "AnagraficaAziendale",
                          key,
                          e.target.value,
                        )
                      }
                    />
                  </div>
                ),
              )}
            </div>
          </div>

          <div className="pos-section-result">
            <div className="pos-section-head">
              <h3>4. Personale e Turni</h3>
              <button
                type="button"
                className="pos-add-btn"
                onClick={() =>
                  addPosArrayItem("PersonaleTurni", {
                    Nome: "",
                    Mansione: "Operaio Specializzato",
                    Turni: "08:00 - 12:00 / 13:00 - 17:00",
                    Prescrizioni: "",
                  })
                }
              >
                + Aggiungi lavoratore
              </button>
            </div>
            {posGenerato.PersonaleTurni?.length ? (
              <div className="pos-edit-table-wrap">
                <table className="pos-edit-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Mansione</th>
                      <th>Turni</th>
                      <th>Prescrizioni Specifiche</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {posGenerato.PersonaleTurni.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            value={item.Nome || ""}
                            onChange={(e) =>
                              updatePosArrayItem(
                                "PersonaleTurni",
                                index,
                                "Nome",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={item.Mansione || ""}
                            onChange={(e) =>
                              updatePosArrayItem(
                                "PersonaleTurni",
                                index,
                                "Mansione",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={item.Turni || ""}
                            onChange={(e) =>
                              updatePosArrayItem(
                                "PersonaleTurni",
                                index,
                                "Turni",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <textarea
                            rows={2}
                            placeholder="Prescrizioni specifiche per il lavoratore (opzionale)"
                            value={item.Prescrizioni || ""}
                            onChange={(e) =>
                              updatePosArrayItem(
                                "PersonaleTurni",
                                index,
                                "Prescrizioni",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="pos-remove-btn"
                            onClick={() =>
                              removePosArrayItem("PersonaleTurni", index)
                            }
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="pos-empty-hint">Nessun lavoratore inserito.</p>
            )}
          </div>

          <div className="pos-section-result">
            <h3>5. Breve Descrizione Lavori</h3>
            <textarea
              className="pos-edit-textarea"
              rows={4}
              value={posGenerato.BreveDescrizioneLavori || ""}
              onChange={(e) =>
                updatePosStringField("BreveDescrizioneLavori", e.target.value)
              }
            />
          </div>

          <div className="pos-section-result">
            <h3>6. Ubicazione e Natura dell'Opera</h3>
            <div className="pos-edit-grid">
              {[
                "ContestoOperativo",
                "AccessibilitaArea",
                "VincoliArea",
                "RetiServiziPresenti",
                "AreeLimitrofeSensibili",
              ].map((key) => (
                <div key={key} className="pos-edit-field full-width">
                  <label>{key}</label>
                  <textarea
                    rows={2}
                    value={posGenerato.UbicazioneNaturaOpera?.[key] || ""}
                    onChange={(e) =>
                      updatePosObjectField(
                        "UbicazioneNaturaOpera",
                        key,
                        e.target.value,
                      )
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="pos-section-result">
            <div className="pos-section-head">
              <h3>7. Valutazione dei Rischi e Procedure</h3>
              <button
                type="button"
                className="pos-add-btn"
                onClick={() =>
                  addPosArrayItem("ValutazioneRischi", {
                    fase: "",
                    rischi: "",
                    prevenzione: "",
                    dpi: "",
                    procedure: "",
                    interferenze: "",
                    probabilita: "",
                    danno: "",
                  })
                }
              >
                + Aggiungi fase
              </button>
            </div>
            {posGenerato.ValutazioneRischi?.length ? (
              <div className="pos-edit-table-wrap">
                <table className="pos-edit-table">
                  <thead>
                    <tr>
                      <th>Fase Lavorativa</th>
                      <th>Rischi</th>
                      <th>Misure di Prevenzione</th>
                      <th>DPI</th>
                      <th>Procedure</th>
                      <th>Interferenze</th>
                      <th>Probabilità (P)</th>
                      <th>Gravità (D)</th>
                      <th>Stima Rischio (P×D)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {posGenerato.ValutazioneRischi.map((item, index) => {
                      const indiceRischio = computeIndiceRischio(
                        item.probabilita,
                        item.danno,
                      );
                      const rischioInfo =
                        indiceRischio != null
                          ? classifyRischio(indiceRischio)
                          : null;
                      return (
                        <tr key={index}>
                          <td>
                            <input
                              value={item.fase || ""}
                              onChange={(e) =>
                                updatePosArrayItem(
                                  "ValutazioneRischi",
                                  index,
                                  "fase",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <textarea
                              rows={2}
                              value={item.rischi || ""}
                              onChange={(e) =>
                                updatePosArrayItem(
                                  "ValutazioneRischi",
                                  index,
                                  "rischi",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <textarea
                              rows={2}
                              value={item.prevenzione || ""}
                              onChange={(e) =>
                                updatePosArrayItem(
                                  "ValutazioneRischi",
                                  index,
                                  "prevenzione",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <input
                              value={item.dpi || ""}
                              onChange={(e) =>
                                updatePosArrayItem(
                                  "ValutazioneRischi",
                                  index,
                                  "dpi",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <textarea
                              rows={2}
                              value={item.procedure || ""}
                              onChange={(e) =>
                                updatePosArrayItem(
                                  "ValutazioneRischi",
                                  index,
                                  "procedure",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <textarea
                              rows={2}
                              value={item.interferenze || ""}
                              onChange={(e) =>
                                updatePosArrayItem(
                                  "ValutazioneRischi",
                                  index,
                                  "interferenze",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <select
                              value={item.probabilita ?? ""}
                              onChange={(e) =>
                                updatePosArrayItem(
                                  "ValutazioneRischi",
                                  index,
                                  "probabilita",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">-</option>
                              <option value="1">1</option>
                              <option value="2">2</option>
                              <option value="3">3</option>
                              <option value="4">4</option>
                            </select>
                          </td>
                          <td>
                            <select
                              value={item.danno ?? ""}
                              onChange={(e) =>
                                updatePosArrayItem(
                                  "ValutazioneRischi",
                                  index,
                                  "danno",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">-</option>
                              <option value="1">1</option>
                              <option value="2">2</option>
                              <option value="3">3</option>
                              <option value="4">4</option>
                            </select>
                          </td>
                          <td>
                            {rischioInfo ? (
                              <span
                                className={`pos-risk-badge pos-risk-badge--${rischioInfo.livello.toLowerCase()}`}
                              >
                                {rischioInfo.livello} ({indiceRischio})
                              </span>
                            ) : (
                              <span className="pos-risk-badge pos-risk-badge--nd">
                                N.D.
                              </span>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="pos-remove-btn"
                              onClick={() =>
                                removePosArrayItem("ValutazioneRischi", index)
                              }
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="pos-empty-hint">
                Nessuna fase di rischio inserita.
              </p>
            )}
          </div>

          <div className="pos-section-result">
            <div className="pos-section-head">
              <h3>8. Gestione Contratti</h3>
              <button
                type="button"
                className="pos-add-btn"
                onClick={() => addPosArrayItem("GestioneContratti", "")}
              >
                + Aggiungi voce
              </button>
            </div>
            {posGenerato.GestioneContratti?.length ? (
              <div className="pos-edit-list">
                {posGenerato.GestioneContratti.map((item, index) => (
                  <div key={index} className="pos-edit-list-row">
                    <input
                      value={item || ""}
                      onChange={(e) =>
                        updatePosArrayStringItem(
                          "GestioneContratti",
                          index,
                          e.target.value,
                        )
                      }
                    />
                    <button
                      type="button"
                      className="pos-remove-btn"
                      onClick={() =>
                        removePosArrayItem("GestioneContratti", index)
                      }
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="pos-empty-hint">Nessuna voce inserita.</p>
            )}
          </div>

          <div className="pos-section-result">
            <div className="pos-section-head">
              <h3>9. Elenco Macchine e Attrezzature</h3>
              <button
                type="button"
                className="pos-add-btn"
                onClick={() => addPosArrayItem("ElencoMacchine", { nome: "" })}
              >
                + Aggiungi macchina
              </button>
            </div>
            {posGenerato.ElencoMacchine?.length ? (
              <div className="pos-edit-list">
                {posGenerato.ElencoMacchine.map((item, index) => (
                  <div key={index} className="pos-edit-list-row">
                    <input
                      value={item.nome || ""}
                      onChange={(e) =>
                        updatePosArrayItem(
                          "ElencoMacchine",
                          index,
                          "nome",
                          e.target.value,
                        )
                      }
                    />
                    <button
                      type="button"
                      className="pos-remove-btn"
                      onClick={() =>
                        removePosArrayItem("ElencoMacchine", index)
                      }
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="pos-empty-hint">Nessuna macchina inserita.</p>
            )}
          </div>

          <div className="pos-section-result">
            <h3>10. Organizzazione del Cantiere</h3>
            <div className="pos-edit-grid">
              {[
                "Descrizione",
                "Recinzione",
                "Accessi",
                "Viabilita",
                "AreeDeposito",
                "Rifiuti",
                "ServiziIgienici",
                "Mensa",
                "ServiziSanitari",
                "Uffici",
                "ImpiantiElettrici",
              ].map((key) => (
                <div key={key} className="pos-edit-field full-width">
                  <label>{key}</label>
                  <textarea
                    rows={2}
                    value={posGenerato.OrganizzazioneCantiere?.[key] || ""}
                    onChange={(e) =>
                      updatePosObjectField(
                        "OrganizzazioneCantiere",
                        key,
                        e.target.value,
                      )
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="pos-section-result">
            <div className="pos-section-head">
              <h3>Ditta Appaltatrice</h3>
              <button
                type="button"
                className="pos-add-btn"
                onClick={handleAddDittaAppaltatrice}
              >
                + Aggiungi ditta appaltatrice
              </button>
            </div>
            {dittaAppaltatrici.length > 0 ? (
              <div className="pos-edit-table-wrap">
                <table className="pos-edit-table">
                  <thead>
                    <tr>
                      <th>Ragione Sociale</th>
                      <th>Indirizzo</th>
                      <th>P. IVA</th>
                      <th>Telefono</th>
                      <th>Email</th>
                      <th>Rappresentante</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {dittaAppaltatrici.map((app, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            value={app.ragioneSociale || ""}
                            onChange={(e) =>
                              handleDittaAppaltatriceChange(
                                index,
                                "ragioneSociale",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={app.indirizzo || ""}
                            onChange={(e) =>
                              handleDittaAppaltatriceChange(
                                index,
                                "indirizzo",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={app.piva || ""}
                            onChange={(e) =>
                              handleDittaAppaltatriceChange(
                                index,
                                "piva",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={app.telefono || ""}
                            onChange={(e) =>
                              handleDittaAppaltatriceChange(
                                index,
                                "telefono",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={app.email || ""}
                            onChange={(e) =>
                              handleDittaAppaltatriceChange(
                                index,
                                "email",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={app.rappresentante || ""}
                            onChange={(e) =>
                              handleDittaAppaltatriceChange(
                                index,
                                "rappresentante",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="pos-remove-btn"
                            onClick={() => handleRemoveDittaAppaltatrice(index)}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="pos-empty-hint">
                Nessuna ditta appaltatrice inserita.
              </p>
            )}
          </div>

          <div className="pos-section-result">
            <div className="pos-section-head">
              <h3>11. Subappaltatori</h3>
              <button
                type="button"
                className="pos-add-btn"
                onClick={handleAddSubappaltatore}
              >
                + Aggiungi subappaltatore
              </button>
            </div>
            {subappaltatori.length > 0 ? (
              <div className="pos-edit-table-wrap">
                <table className="pos-edit-table">
                  <thead>
                    <tr>
                      <th>Ragione Sociale</th>
                      <th>Indirizzo</th>
                      <th>P. IVA</th>
                      <th>Telefono</th>
                      <th>Email</th>
                      <th>Rappresentante</th>
                      <th>Opere Assegnate</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {subappaltatori.map((sub, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            value={sub.ragioneSociale || ""}
                            onChange={(e) =>
                              handleSubappaltatoreChange(
                                index,
                                "ragioneSociale",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={sub.indirizzo || ""}
                            onChange={(e) =>
                              handleSubappaltatoreChange(
                                index,
                                "indirizzo",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={sub.piva || ""}
                            onChange={(e) =>
                              handleSubappaltatoreChange(
                                index,
                                "piva",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={sub.telefono || ""}
                            onChange={(e) =>
                              handleSubappaltatoreChange(
                                index,
                                "telefono",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={sub.email || ""}
                            onChange={(e) =>
                              handleSubappaltatoreChange(
                                index,
                                "email",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={sub.rappresentante || ""}
                            onChange={(e) =>
                              handleSubappaltatoreChange(
                                index,
                                "rappresentante",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={sub.opereAssegnate || ""}
                            onChange={(e) =>
                              handleSubappaltatoreChange(
                                index,
                                "opereAssegnate",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="pos-remove-btn"
                            onClick={() => handleRemoveSubappaltatore(index)}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="pos-empty-hint">Nessun subappaltatore inserito.</p>
            )}
          </div>

          <div className="pos-section-result">
            <details>
              <summary className="pos-json-summary">
                JSON revisionabile (modalità avanzata)
              </summary>
              <div className="pos-json-editor-actions">
                <button
                  type="button"
                  className="pos-json-btn"
                  onClick={() => {
                    try {
                      const parsed = normalizePosOutput(
                        JSON.parse(posJsonDraft || ""),
                        formData,
                      );
                      const pretty = JSON.stringify(parsed, null, 2);
                      setPosGenerato(parsed);
                      setPosJsonDraft(pretty);
                      setPosJsonDirty(false);
                      setPosJsonError(null);
                    } catch (e) {
                      setPosJsonError(
                        e?.message || "JSON non valido: impossibile applicare.",
                      );
                    }
                  }}
                  disabled={!posJsonDirty}
                >
                  Applica JSON revisionato
                </button>
                <button
                  type="button"
                  className="pos-json-btn secondary"
                  onClick={() => {
                    if (!posJsonSource) return;
                    setPosGenerato(posJsonSource);
                    setPosJsonDraft(posJsonSourceText);
                    setPosJsonDirty(false);
                    setPosJsonError(null);
                  }}
                  disabled={!posJsonSource}
                >
                  Ripristina JSON originale
                </button>
              </div>
              {posJsonError && (
                <div className="pos-json-error">{posJsonError}</div>
              )}
              <textarea
                className="pos-json-editor"
                value={posJsonDraft}
                onChange={(e) => {
                  setPosJsonDraft(e.target.value);
                  setPosJsonDirty(true);
                  if (posJsonError) setPosJsonError(null);
                }}
                rows={18}
                spellCheck={false}
              />
            </details>
          </div>
        </div>
      )}

      {duplicaOpen && (
        <div
          className="pos-duplica-overlay"
          onClick={(e) => {
            if (e.target.classList.contains("pos-duplica-overlay")) {
              setDuplicaOpen(false);
            }
          }}
        >
          <div className="pos-duplica-modal">
            <div className="pos-duplica-header">
              <h2>Nuovo POS con duplica</h2>
              <button
                type="button"
                className="pos-duplica-close"
                onClick={() => setDuplicaOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="pos-duplica-body">
              <div className="pos-duplica-field">
                <label>Seleziona POS da archivio</label>
                <Select
                  value={
                    duplicaSelectedPosId
                      ? posDuplicaOptions.find(
                          (o) => o.value === duplicaSelectedPosId,
                        ) || null
                      : null
                  }
                  onChange={(opt) => {
                    const nextPosId = opt?.value || "";
                    setDuplicaSelectedPosId(nextPosId);
                    const group =
                      nextPosId &&
                      (archivioGroups || []).find((g) => g.posId === nextPosId);
                    const nextDocId = group?.versions?.[0]?.id || "";
                    setDuplicaSelectedDocId(nextDocId);
                  }}
                  options={posDuplicaOptions}
                  placeholder="Seleziona un POS..."
                  isSearchable
                  menuPortalTarget={portalTarget}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
              <div className="pos-duplica-field">
                <label>Seleziona versione</label>
                <Select
                  value={
                    duplicaSelectedDocId
                      ? duplicaVersionOptions.find(
                          (o) => o.value === duplicaSelectedDocId,
                        ) || null
                      : null
                  }
                  onChange={(opt) => setDuplicaSelectedDocId(opt?.value || "")}
                  options={duplicaVersionOptions}
                  placeholder="Seleziona una versione..."
                  isSearchable={false}
                  menuPortalTarget={portalTarget}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </div>
            <div className="pos-duplica-actions">
              <button
                type="button"
                className="pos-json-btn secondary"
                onClick={() => setDuplicaOpen(false)}
              >
                Annulla
              </button>
              <button
                type="button"
                className="pos-json-btn"
                onClick={handleDuplicaConfirm}
                disabled={!duplicaSelectedDocId}
              >
                Duplica e compila
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
